import { LOCAL_STORAGE_KEY_JWT, LOCAL_STORAGE_KEY_POST_LOGIN_REDIRECT } from '@/api'
import { clearUserLocalStorage, sendMessageToExtension } from '@/utils'
import { useLocalStorage } from '@/utils/useLocalStorage'
import { useCallback } from 'react'

const getEmailFromJwt = (jwt: string): string => {
  try {
    const payload = jwt.split('.')[1]
    const decoded = JSON.parse(atob(payload))
    return decoded.sub || decoded.email || '-'
  } catch {
    return '-'
  }
}

export const isSafeRedirectPath = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length < 2048 &&
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.includes('\\')

const consumeSavedRedirect = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY_POST_LOGIN_REDIRECT)
    if (!raw) return null
    window.localStorage.removeItem(LOCAL_STORAGE_KEY_POST_LOGIN_REDIRECT)
    const value = JSON.parse(raw)
    return isSafeRedirectPath(value) ? value : null
  } catch {
    return null
  }
}

const appendInviteToPath = (path: string, inviteCode: string): string => {
  const [pathAndQuery, hash = ''] = path.split('#')
  const [basePath, existingQuery = ''] = pathAndQuery.split('?')
  const params = new URLSearchParams(existingQuery)
  if (!params.get('invite')) {
    params.set('invite', inviteCode)
  }
  const query = params.toString()
  return `${basePath}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`
}

export const useAuthComplete = () => {
  const [, setJwt] = useLocalStorage(LOCAL_STORAGE_KEY_JWT, '')

  const complete = useCallback(
    async (jwt: string, inviteCode?: string) => {
      const savedRedirect = consumeSavedRedirect()

      clearUserLocalStorage()
      setJwt(jwt)

      const email = getEmailFromJwt(jwt)

      try {
        await sendMessageToExtension<void>('login', { email, jwt })
      } catch (error) {
        console.error('Failed to sync with extension:', error)
      }

      const basePath = savedRedirect ?? '/'
      const redirectUrl = inviteCode ? appendInviteToPath(basePath, inviteCode) : basePath
      window.location.replace(redirectUrl)
    },
    [setJwt]
  )

  return { complete }
}
